/**
 * One small change to an organisation team, saved the way the team editor saves (TeamEditor.tsx): the record as the
 * platform holds it now, the change applied, the whole body put back through `PUT /portfolio/v1/teams/:id`. The
 * platform merges field by field and says what it kept (`refused`) and dropped (`ignored`); both come back in words.
 */

import type { OrgTeamV2 } from '@aioe/shared';

import { routes } from '../../api/client.ts';
import { editable, ignoredWords, refusedWords, saveErrorWords, toBody, type EditableTeam } from './teamsModel.ts';

/** The record the editor works on, as the platform holds it now; undefined when it is gone or the caller may not edit. */
export async function teamRecord(teamId: string, kind: 'team' | 'specialist'): Promise<OrgTeamV2 | undefined> {
  return (await routes.teams(kind === 'specialist' ? 'specialist' : undefined)).find((t) => t.id === teamId);
}

/** Apply `change` to the stored team and save it; the words of anything the platform kept or left out, or throws in words. */
export async function saveTeamChange(teamId: string, kind: 'team' | 'specialist', change: (d: EditableTeam) => EditableTeam): Promise<string[]> {
  const doc = await teamRecord(teamId, kind);
  if (!doc) throw new Error('This team can no longer be changed here: it may have been removed.');
  const d = change(editable(doc));
  try {
    const answer = await routes.saveTeam(
      {
        ...toBody({ name: d.name, description: d.description ?? '', lead: d.lead ?? '', kind: d.kind, members: d.members, settings: d.settings, ...(d.grants ? { grants: d.grants } : {}) }),
        id: d.id,
      },
      false,
    );
    return [...refusedWords(answer.refused ?? []), ...(answer.ignored?.length ? [ignoredWords(answer.ignored)] : [])];
  } catch (e) {
    throw new Error(saveErrorWords(e), { cause: e });
  }
}
